import { Fragment, useState } from "react"
import {
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import { API_CONTRACTS, curlSnippet, fetchSnippet } from "./apiContracts"

const METHOD_COLOR: Record<string, string> = {
  GET: "success.main",
  POST: "info.main",
  PUT: "warning.dark",
}

function MethodChip({ method }: { method: string }) {
  return (
    <Typography
      component="span"
      variant="caption"
      sx={{
        color: METHOD_COLOR[method] ?? "text.secondary",
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        minWidth: 38,
      }}
    >
      {method}
    </Typography>
  )
}

// The API dialog: every contract this console and its callers rely on, with
// copyable request snippets and realistic responses. Mirrors the API dialog
// in the document-generation service: grouped endpoint list on the left,
// method + path + request/response on the right.
export function ApiDialog({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const [selectedKey, setSelectedKey] = useState(API_CONTRACTS[0].key)
  const [lang, setLang] = useState<"curl" | "fetch">("curl")
  const contract =
    API_CONTRACTS.find((entry) => entry.key === selectedKey) ?? API_CONTRACTS[0]
  const tags = [...new Set(API_CONTRACTS.map((entry) => entry.tag))]
  const snippet =
    lang === "curl" ? curlSnippet(contract) : fetchSnippet(contract)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: "divider" }}
      >
        <Typography variant="h6">API</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "max-content minmax(0, 1fr)",
          height: "min(80vh, 48rem)",
          minHeight: 0,
        }}
      >
        <Box
          component="aside"
          sx={{
            borderRight: 1,
            borderColor: "divider",
            bgcolor: "action.hover",
            overflowY: "auto",
            py: 1,
          }}
        >
          {tags.map((tag) => (
            <Fragment key={tag}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  px: 2,
                  pt: 2,
                  pb: 0.5,
                  color: "text.secondary",
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                  fontSize: "0.625rem",
                }}
              >
                {tag}
              </Typography>
              {API_CONTRACTS.filter((entry) => entry.tag === tag).map(
                (entry) => (
                  <Stack
                    key={entry.key}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    onClick={() => setSelectedKey(entry.key)}
                    sx={{
                      px: 2,
                      py: 0.75,
                      cursor: "pointer",
                      bgcolor:
                        entry.key === contract.key
                          ? "action.selected"
                          : "transparent",
                      "&:hover": { bgcolor: "action.selected" },
                    }}
                  >
                    <MethodChip method={entry.method} />
                    <Typography variant="caption" noWrap>
                      {entry.path.split("?")[0].split("/").slice(-1)[0]}
                    </Typography>
                  </Stack>
                ),
              )}
            </Fragment>
          ))}
        </Box>

        <Box sx={{ overflowY: "auto", minHeight: 0 }}>
          <Box
            sx={{
              px: 4,
              pt: 3,
              pb: 2,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
              <MethodChip method={contract.method} />
              <Typography
                component="code"
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  bgcolor: "action.hover",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 0.5,
                  minWidth: 0,
                  flex: 1,
                  overflowWrap: "anywhere",
                }}
              >
                {contract.path}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {contract.summary}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={contract.auth}
              sx={{ height: 22, fontSize: "0.72rem" }}
            />
          </Box>

          <Stack spacing={3} sx={{ px: 4, py: 3 }}>
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="subtitle2">Request</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={lang}
                    onChange={(_, value) => value && setLang(value)}
                  >
                    <ToggleButton value="curl" sx={{ py: 0, px: 1 }}>
                      curl
                    </ToggleButton>
                    <ToggleButton value="fetch" sx={{ py: 0, px: 1 }}>
                      fetch
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <IconButton
                    size="small"
                    onClick={() => navigator.clipboard.writeText(snippet)}
                    title="Copy"
                  >
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>
              </Stack>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  bgcolor: "action.hover",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1.5,
                  fontSize: "0.78rem",
                  overflowX: "auto",
                }}
              >
                {snippet}
              </Box>
            </Box>

            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="subtitle2">Response</Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: "monospace" }}
                >
                  {contract.responseStatus}
                </Typography>
              </Stack>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  bgcolor: "action.hover",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1.5,
                  fontSize: "0.78rem",
                  overflowX: "auto",
                }}
              >
                {JSON.stringify(contract.exampleResponse, null, 2)}
              </Box>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Dialog>
  )
}

export function ApiDialogButton() {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{ textTransform: "none", borderRadius: 99, px: 3 }}
      >
        API
      </Button>
      {open && <ApiDialog open onClose={() => setOpen(false)} />}
    </>
  )
}
